import React from 'react';
import { i18n } from 'config/locales';

import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { HistoryEmploymentItemModel } from 'forms/sections/history/employment/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import classnames from 'classnames';
import { Dispatch } from 'redux';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Branch from 'components/Form/Branch';
import Field from 'components/Form/Field';
import Telephone from 'components/Form/Telephone';
import Location from 'components/Form/Location';
import Textarea from 'components/Form/Textarea';
import ErrorMessage from 'components/ErrorMessage';
import Comments from 'components/Form/Comments';

type PhysicalAddressType = HistoryEmploymentItemModel['PhysicalAddress']

interface Props extends ValidationElementProps, PhysicalAddressType {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  title?: string
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  className?: string
  addressBooks?: CommonAddressBooks
  addressBook?: any
  attribute?: string
  PhysicalLocationComments?: any
  // TODO: Remove.
  dispatch?: Dispatch
}

export default class PhysicalAddress extends ValidationElement<Props> {
  update = (queue: Partial<PhysicalAddressType>) => {
    this.props.onUpdate({
      name: this.props.name,
      HasDifferentAddress: this.props.HasDifferentAddress,
      Address: this.props.Address,
      Telephone: this.props.Telephone,
      PhysicalAddressAnswerComments: this.props.PhysicalAddressAnswerComments,
      PhysicalAddressComments: this.props.PhysicalAddressComments,
      ...queue,
    });
  };

  /**
   * Handle the change event.
   */
  updateBranch = (values: PhysicalAddressType['HasDifferentAddress']) => {
    this.update({
      HasDifferentAddress: values,
      Address: values.value !== 'Yes' ? {} : this.props.Address,
    });
  };

  updateAddress = (values: PhysicalAddressType['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateTelephone = (values: PhysicalAddressType['Telephone']) => {
    this.update({
      Telephone: values,
    });
  };

  updatePhysicalAddressAnswerComments = (values: PhysicalAddressType['PhysicalAddressAnswerComments']) => {
    this.update({
      PhysicalAddressAnswerComments: values,
    });
  };

  updatePhysicalAddressComments = (values: PhysicalAddressType['PhysicalAddressComments']) => {
    this.update({
      PhysicalAddressComments: values,
    });
  };

  options = () => (
    <Branch
      attribute={setAttribute(this.props.attribute, 'HasDifferentAddress')}
      label={this.props.title}
      labelSize="h4"
      name="physicalAddress"
      {...this.props.HasDifferentAddress}
      className="has-different-address"
      help="history.employment.default.physicalAddress.help"
      onUpdate={this.updateBranch}
      onError={this.props.onError}
      required={this.props.required}
      scrollIntoView={this.props.scrollIntoView}
      warning
    />
  );

  isPossiblyTelecommute = () => {
    const { Address, EmploymentAddress } = this.props;
    return Address?.state
      && EmploymentAddress?.state
      && Address.state !== EmploymentAddress.state;
  };

  render() {
    const className = classnames('physical', this.props.className);
    const Options = this.options;

    if ((this.props.HasDifferentAddress || {}).value === 'Yes') {
      return (
        <div className="has-different">
          <div className={classnames(className, 'physical-address')}>
            <Options />
          </div>
          <Comments
            title={i18n.t('history.employment.default.physicalAddress.answercomments')}
            name="PhysicalAddressAnswerComments"
            className="optional-comment"
            attribute="PhysicalAddressAnswerComments"
            {...this.props.PhysicalAddressAnswerComments}
            onUpdate={this.props.updatePhysicalAddressAnswerComments || this.updatePhysicalAddressAnswerComments}
            required={false}
          />
          <Field
            attribute={setAttribute(this.props.attribute, 'Address')}
            title={i18n.t('history.employment.default.physicalAddress.heading.address')}
            titleSize="h4"
            optional
            help="history.employment.default.physicalAddress.address.help"
            adjustFor="labels"
            shrink
            scrollIntoView={this.props.scrollIntoView}
          >
            <Location
              attribute={setAttribute(this.props.attribute, 'Address')}
              name="address"
              className="physical-address-address"
              {...this.props.Address}
              label={i18n.t('history.employment.default.physicalAddress.address.label')}
              layout={Location.ADDRESS}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook={this.props.addressBook}
              showPostOffice
              dispatch={this.props.dispatch}
              onUpdate={this.updateAddress}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={setAttribute(this.props.attribute, 'Telephone')}
            title={i18n.t('history.employment.default.physicalAddress.heading.telephone')}
            titleSize="h4"
            className="override-required"
            adjustFor="telephone"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Telephone
              attribute={setAttribute(this.props.attribute, 'Telephone')}
              name="telephone"
              {...this.props.Telephone}
              className="physical-address-telephone"
              onUpdate={this.updateTelephone}
              allowNotApplicable={false}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          <Comments
            title={i18n.t('history.employment.default.physicalAddress.addresscomments')}
            name="PhysicalAddressComments"
            className="optional-comment"
            attribute="PhysicalAddressComments"
            {...this.props.PhysicalAddressComments}
            onUpdate={this.props.updatePhysicalAddressComments || this.updatePhysicalAddressComments}
            required={false}
          />
        </div>
      );
    }

    return (
      <>
        <div className={classnames(className, 'physical-address')}>
          <Options />
        </div>
        <Field
          title={i18n.t('history.employment.default.physicalAddress.answercomments')}
          attribute="PhysicalAddressAnswerComments"
          titleSize="label"
          optional
        >
          <Textarea
            title="Optional Comments"
            name="PhysicalAddressAnswerComments"
            className="optional-comment"
            attribute="PhysicalAddressAnswerComments"
            {...this.props.PhysicalAddressAnswerComments}
            onUpdate={this.props.updatePhysicalAddressAnswerComments || this.updatePhysicalAddressAnswerComments}
            required={false}
          />
        </Field>
        {(this.props.HasDifferentAddress || {}).value === 'Yes' && (
          <div className="has-different">
            <Field
              attribute={setAttribute(this.props.attribute, 'Address')}
              title={i18n.t('history.employment.default.physicalAddress.heading.address')}
              titleSize="h4"
              optional
              help="history.employment.default.physicalAddress.address.help"
              adjustFor="labels"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <Location
                attribute={setAttribute(this.props.attribute, 'Address')}
                name="address"
                className="physical-address-address"
                {...this.props.Address}
                label={i18n.t('history.employment.default.physicalAddress.address.label')}
                layout={Location.ADDRESS}
                geocode
                addressBooks={this.props.addressBooks}
                addressBook={this.props.addressBook}
                showPostOffice
                dispatch={this.props.dispatch}
                onUpdate={this.updateAddress}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

            <Field
              attribute={setAttribute(this.props.attribute, 'Telephone')}
              title={i18n.t('history.employment.default.physicalAddress.heading.telephone')}
              titleSize="h4"
              className="override-required"
              adjustFor="telephone"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Telephone
                attribute={setAttribute(this.props.attribute, 'Telephone')}
                name="telephone"
                {...this.props.Telephone}
                className="physical-address-telephone"
                onUpdate={this.updateTelephone}
                allowNotApplicable={false}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            {this.isPossiblyTelecommute() && (
              <ErrorMessage
                isWarning
                title="Your address of employment and work address where you are/were physically located are not within the same state"
                message={[
                  'Please confirm that this is correct.',
                  'If you work/worked remotely, please add a comment that you "teleworked for position" in the optional comments below.'
                ]}
              />
            )}
            <Field
              title={i18n.t('history.employment.default.physicalAddress.addresscomments')}
              attribute="PhysicalAddressComments"
              titleSize="label"
              optional
            >
              <Textarea
                title="Optional Comments"
                name="PhysicalAddressComments"
                className="optional-comment"
                attribute="PhysicalAddressComments"
                {...this.props.PhysicalAddressComments}
                onUpdate={this.props.updatePhysicalAddressComments || this.updatePhysicalAddressComments}
                required={false}
              />
            </Field>
          </div>
        )}
      </>
    );
  }
}

PhysicalAddress.defaultProps = {
  title: '',
  HasDifferentAddress: {},
  PhysicalAddressAnswerComments: {},
  PhysicalAddressComments: {},
  addressBooks: {},
  addressBook: 'Company',
  dispatch: (action) => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
